import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import { MAIN_SITE_URL } from '@/lib/config'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { FormBuilder, DEFAULT_FORM_SCHEMA } from '@/components/careers/FormBuilder'
import { QueryError } from '@/components/ui/QueryError'
import type { CareerPosting, FormSchema } from '@/types/careers'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
  EmptyState,
  LoadingRows,
} from '@/components/ui/DataTable'

type EditorTab = 'details' | 'form'

const emptyPosting = (): Partial<CareerPosting> & { formSchema: FormSchema } => ({
  title: '',
  department: '',
  description: '',
  requirements: '',
  applyIntro: '',
  applicationMode: 'builtin',
  applicationLink: '',
  sortOrder: 0,
  formSchema: DEFAULT_FORM_SCHEMA,
})

export function CareersPostingsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<(Partial<CareerPosting> & { formSchema: FormSchema }) | null>(null)
  const [editorTab, setEditorTab] = useState<EditorTab>('details')
  const [error, setError] = useState('')

  const { data, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['career-postings'],
    queryFn: () => api<{ postings: CareerPosting[] }>('/api/careers/postings'),
  })

  const postings = data?.postings || []

  const saveMutation = useMutation({
    mutationFn: async (posting: Partial<CareerPosting> & { formSchema: FormSchema }) => {
      const body = {
        title: posting.title,
        department: posting.department,
        description: posting.description,
        requirements: posting.requirements,
        applyIntro: posting.applyIntro || null,
        sortOrder: posting.sortOrder ?? 0,
        applicationMode: posting.applicationMode || 'builtin',
        applicationLink: posting.applicationMode === 'external_link' ? posting.applicationLink : null,
        formSchema: posting.applicationMode === 'builtin' ? posting.formSchema : undefined,
      }
      if (posting.id) {
        return api(`/api/careers/postings/${posting.id}`, { method: 'PUT', body: JSON.stringify(body) })
      }
      return api<{ id: number }>('/api/careers/postings', { method: 'POST', body: JSON.stringify(body) })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['career-postings'] })
      setEditing(null)
      setError('')
    },
    onError: (e: Error) => setError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api(`/api/careers/postings/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['career-postings'] }),
    onError: (e: Error) => setError(e.message),
  })

  function savePosting() {
    if (!editing) return
    if (!editing.title?.trim() || !editing.department?.trim() || !editing.description?.trim()) {
      setError('Title, department, and role description are required.')
      setEditorTab('details')
      return
    }
    if (editing.applicationMode === 'external_link') {
      try {
        const url = new URL(editing.applicationLink || '')
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error()
      } catch {
        setError('Enter a complete https:// application URL.')
        setEditorTab('details')
        return
      }
    }
    setError('')
    saveMutation.mutate(editing)
  }

  function openCreate() {
    setEditing(emptyPosting())
    setEditorTab('details')
    setError('')
  }

  function openEdit(p: CareerPosting) {
    setEditing({
      ...p,
      formSchema: p.formSchema || DEFAULT_FORM_SCHEMA,
    })
    setEditorTab('details')
    setError('')
  }

  return (
    <div>
      <PageHeader
        title="Career postings"
        description="Manage roles shown on the public careers page. Each role can use a built-in application form with custom sections and questions."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> New posting
          </Button>
        }
      />

      {editing && (
        <div className="admin-editor-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="text-lg font-semibold text-[#f0f4f8]">
              {editing.id ? 'Edit posting' : 'New posting'}
            </h2>
            <div className="admin-editor-tabs">
              <button
                type="button"
                className={editorTab === 'details' ? 'is-active' : ''}
                onClick={() => setEditorTab('details')}
              >
                Role details
              </button>
              <button
                type="button"
                className={editorTab === 'form' ? 'is-active' : ''}
                onClick={() => setEditorTab('form')}
                disabled={editing.applicationMode === 'external_link'}
              >
                Application form
              </button>
            </div>
          </div>

          {editorTab === 'details' ? (
            <div className="grid gap-4 max-w-2xl">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-[#9aabbb] mb-1 block">Title</span>
                  <Input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-xs text-[#9aabbb] mb-1 block">Department</span>
                  <Input value={editing.department || ''} onChange={(e) => setEditing({ ...editing, department: e.target.value })} />
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-[#9aabbb] mb-1 block">About the role</span>
                <textarea
                  className="w-full min-h-[120px] rounded-[10px] border border-[rgba(255,255,255,0.1)] bg-[rgba(6,9,12,0.6)] px-3 py-2 text-sm text-[#f0f4f8]"
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#9aabbb] mb-1 block">Requirements</span>
                <textarea
                  className="w-full min-h-[100px] rounded-[10px] border border-[rgba(255,255,255,0.1)] bg-[rgba(6,9,12,0.6)] px-3 py-2 text-sm text-[#f0f4f8]"
                  value={editing.requirements || ''}
                  onChange={(e) => setEditing({ ...editing, requirements: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#9aabbb] mb-1 block">Apply intro (shown before the form)</span>
                <textarea
                  className="w-full min-h-[80px] rounded-[10px] border border-[rgba(255,255,255,0.1)] bg-[rgba(6,9,12,0.6)] px-3 py-2 text-sm text-[#f0f4f8]"
                  value={editing.applyIntro || ''}
                  onChange={(e) => setEditing({ ...editing, applyIntro: e.target.value })}
                />
              </label>
              <label className="block max-w-xs">
                <span className="text-xs text-[#9aabbb] mb-1 block">Sort order</span>
                <Input
                  type="number"
                  value={editing.sortOrder ?? 0}
                  onChange={(e) => setEditing({ ...editing, sortOrder: parseInt(e.target.value, 10) || 0 })}
                />
              </label>
              <fieldset className="space-y-2">
                <legend className="text-xs text-[#9aabbb]">Application method</legend>
                <label className="flex items-center gap-2 text-sm text-[#f0f4f8]">
                  <input
                    type="radio"
                    checked={editing.applicationMode !== 'external_link'}
                    onChange={() => setEditing({ ...editing, applicationMode: 'builtin' })}
                  />
                  Built-in form (recommended)
                </label>
                <label className="flex items-center gap-2 text-sm text-[#f0f4f8]">
                  <input
                    type="radio"
                    checked={editing.applicationMode === 'external_link'}
                    onChange={() => setEditing({ ...editing, applicationMode: 'external_link' })}
                  />
                  External link
                </label>
              </fieldset>
              {editing.applicationMode === 'external_link' && (
                <label className="block">
                  <span className="text-xs text-[#9aabbb] mb-1 block">External application URL</span>
                  <Input
                    value={editing.applicationLink || ''}
                    onChange={(e) => setEditing({ ...editing, applicationLink: e.target.value })}
                    placeholder="https://…"
                  />
                </label>
              )}
            </div>
          ) : (
            <FormBuilder
              value={editing.formSchema}
              onChange={(formSchema) => setEditing({ ...editing, formSchema })}
            />
          )}

          {error && <p className="mt-4 text-sm text-[#fca5a5]">{error}</p>}

          <div className="flex gap-3 mt-6">
            <Button onClick={savePosting} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save posting'}
            </Button>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!editing && error && <p className="mb-4 text-sm text-[#fca5a5]" role="alert">{error}</p>}

      {queryError ? (
        <QueryError title="Couldn’t load job listings" error={queryError} onRetry={() => void refetch()} />
      ) : <DataTable>
        <table className="w-full min-w-[720px]">
          <DataTableHead>
            <DataTableHeaderCell>Role</DataTableHeaderCell>
            <DataTableHeaderCell>Department</DataTableHeaderCell>
            <DataTableHeaderCell>Application</DataTableHeaderCell>
            <DataTableHeaderCell className="text-right">Actions</DataTableHeaderCell>
          </DataTableHead>
          {isLoading ? (
            <LoadingRows cols={4} />
          ) : postings.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={4}>
                  <EmptyState title="No postings yet" description="Create a role to show it on the careers page." />
                </td>
              </tr>
            </tbody>
          ) : (
            <DataTableBody>
              {postings.map((p) => (
                <DataTableRow key={p.id}>
                  <DataTableCell className="font-medium text-[#f0f4f8]">{p.title}</DataTableCell>
                  <DataTableCell className="text-[#9aabbb]">{p.department}</DataTableCell>
                  <DataTableCell>
                    <Badge variant={p.applicationMode === 'external_link' ? 'muted' : 'default'}>
                      {p.applicationMode === 'external_link' ? 'External link' : 'Built-in form'}
                    </Badge>
                  </DataTableCell>
                  <DataTableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <a
                        href={`${MAIN_SITE_URL}/careers.html?role=${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9aabbb] hover:text-[#40dbf1] hover:bg-[rgba(64,219,241,0.08)]"
                        title="Preview on careers page"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9aabbb] hover:text-[#40dbf1] hover:bg-[rgba(64,219,241,0.08)]"
                        onClick={() => openEdit(p)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9aabbb] hover:text-[#fca5a5] hover:bg-[rgba(239,68,68,0.08)]"
                        onClick={() => {
                          if (confirm(`Delete "${p.title}"?`)) deleteMutation.mutate(p.id)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          )}
        </table>
      </DataTable>}
    </div>
  )
}
